import * as React from 'react';
import { Formik, FormikConsumer, FormikHelpers } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import * as commonStyles from '../../../common/common.module.scss';
import * as styles from './client-admin.module.scss';
import Client from '../../../../models/client';
import PrimaryButton from '../../../common/primary-button';
import { CircularProgress } from '@material-ui/core';
import * as Yup from 'yup';
import usStates from './us-states.json';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import SwitchBox from '../../../common/switchBox';
import useClient from '../../../../hooks/useClient';
import Paths from '../../../../models/enums/paths';
import { useRef } from 'react';
import {
  OutlinedInput,
  OutlinedSelect,
  PhoneInputMask,
  SecondaryAlert,
  SecondaryButton,
  TertiaryButton
} from 'kagr-ui';
import AdministratorTransfer from './manageClientForm/adminstrator-transfer';
import SupportTypeTransfer from './manageClientForm/support-type-transfer';
import ServiceDeskFields from './manageClientForm/service-desk-fields';
import { useAdministrators } from '../../../../hooks/useAdministrators';
import { useSupportTypes } from '../../../../hooks/useSupportTypes';
import { useSupportFields } from '../../../../hooks/useSupportFields';
import NavigationWarning from '../../navigationWarning';

interface Props extends RouteComponentProps {
  /** True is loading existing client */
  loading?: boolean;
  client: Client;
  /** True is creating a new client. False if editing an existing client. */
  isNewClient: boolean;
}

const useStyles = makeStyles({
  brandingInput: {
    display: 'none'
  }
});

const ManageClientSchema = Yup.object().shape({
  name: Yup.string().required('Name is a required field.'),
  displayName: Yup.string().required('Display name is a required field.'),
  addressOne: Yup.string().required('Address is a required field.'),
  city: Yup.string().required('City is a required field.'),
  state: Yup.number().integer().positive('State is a required field'),
  zipCode: Yup.string()
    .matches(/^\d{5}$/, 'Zip code must be exactly 5 digits.')
    .required('Zip code is a required field.'),
  firstName: Yup.string().required('First name is a required field.'),
  lastName: Yup.string().required('Last name is a required field.'),
  email: Yup.string().email('Must be valid email.').required('Email is a required field.'),
  phone: Yup.string()
    .matches(/^\([1-9]\d{2}\)\ \d{3}\-\d{4}$|[1-9]\d{9}/, 'Phone must be exactly 10 digits.')
    .required('Phone is a required field.'),
  supportEmail: Yup.string().email('Must be valid email.').required('Support Email is a required field.')
});

/**
 * @function ClientManager Allows a user to view/create/edit a single client's information.
 */
const ClientManager = ({ loading: loadingClient, client, isNewClient, history }: Props) => {
  const classes = useStyles();
  const adminsQuery = useAdministrators();
  const orgsQuery = useSupportTypes('organizations');
  const reqTypesQuery = useSupportTypes('request-types');
  const fieldsQuery = useSupportFields();
  const { createClient, storeLogo, fetchClientBrandImage, editClient } = useClient();
  const [mutationError, setMutationError] = React.useState<string>('');
  //Brand image file for uploading
  const [brandImageFile, setBrandImageFile] = React.useState<File>(null);
  //Brand image for viewing
  const [brandImageSrc, setBrandImageSrc] = React.useState<string>('');

  const [displayDirtyWarning, setDisplayDirtyWarning] = React.useState<boolean>(false);
  const [confirmedNavigation, setConfirmedNavigation] = React.useState<boolean>(false);
  const [blockedLocation, setBlockedLocation] = React.useState<string>('');

  const unblock = useRef(null);

  const formikRef = useRef(null);

  React.useEffect(() => {
    // Block history actions and register a function to call instead, unblocking if the form is unchanged
    unblock.current = history.block((location) => {
      if (formikRef.current !== null && formikRef.current.dirty) {
        setDisplayDirtyWarning(true);
        setBlockedLocation(location.pathname);
        return false;
      } else {
        unblock.current();
      }
    });
  }, []);

  React.useEffect(() => {
    if (confirmedNavigation) {
      unblock.current();
      history.push(blockedLocation);
    }
  }, [confirmedNavigation]);

  React.useEffect(() => {
    if (brandImageFile !== null) {
      let reader = new FileReader();

      reader.onload = (event) => {
        setBrandImageSrc(event.target.result as string);
      };

      reader.readAsDataURL(brandImageFile);
    }
  }, [brandImageFile]);

  React.useEffect(() => {
    if (!isNewClient && !loadingClient && client.brandImage != null && client.brandImage !== '') {
      setBrandImageSrc(fetchClientBrandImage(client.brandImage));
    }
  }, [loadingClient, isNewClient]);

  const handleCancel = (dirty: boolean) => {
    if (!dirty) {
      unblock.current();
    }
    history.push(Paths.Home);
  };

  const handleSubmit = async (values: Client, { setSubmitting }: FormikHelpers<Client>) => {
    setSubmitting(true);

    values.phone = values.phone.match(/\d+/g).join('');

    try {
      // message is a string containing the integer id of the updated client
      let response: { message: string; success: boolean; statusCode: number };
      if (isNewClient) {
        response = await createClient(values);
      } else {
        response = await editClient(values);
      }
      if (response.success && brandImageFile != null) {
        await storeLogo(Number(response.message), brandImageFile);
      }
      setSubmitting(false);
      unblock.current();
      history.push(Paths.Home);
    } catch (err) {
      setMutationError(err.message);
      setSubmitting(false);
    }
  };

  if (
    loadingClient ||
    adminsQuery.isLoading ||
    orgsQuery.isLoading ||
    reqTypesQuery.isLoading ||
    fieldsQuery.isLoading
  ) {
    return (
      <div className="loadingIndicator">
        <CircularProgress />
      </div>
    );
  }
  if (adminsQuery.isError || orgsQuery.isError || reqTypesQuery.isError || fieldsQuery.isError) {
    return <SecondaryAlert type="error" message="Error loading management options" />;
  }
  return (
    <React.Fragment>
      {mutationError && <SecondaryAlert type="error" message={mutationError} />}
      <Formik
        innerRef={formikRef}
        initialValues={client}
        validationSchema={ManageClientSchema}
        onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers)}
      >
        {(formik) => (
          <form>
            <FormikConsumer>
              {(formik) => (
                <NavigationWarning
                  formik={formik}
                  displayDirtyWarning={displayDirtyWarning}
                  confirmNavigationWarning={() => {
                    setConfirmedNavigation(true);
                    history.push(blockedLocation);
                  }}
                  cancleNavigationWarning={() => setDisplayDirtyWarning(false)}
                />
              )}
            </FormikConsumer>
            {formik.isSubmitting && (
              <div className={commonStyles.overlay}>
                <div className={commonStyles.fixedCircularProgress}>
                  <CircularProgress />
                </div>
              </div>
            )}
            <fieldset className={commonStyles.fieldset}>
              <h2 className={styles.formSectionHeading}>Details</h2>
              <div className={commonStyles.twoColumn}>
                <OutlinedInput
                  id="name"
                  label="Name"
                  name="name"
                  {...formik.getFieldProps('name')}
                  error={formik.errors.name && formik.touched.name}
                  helperText={formik.touched.name && formik.errors.name}
                  required
                />
                <OutlinedInput
                  id="displayName"
                  label="Display Name"
                  {...formik.getFieldProps('displayName')}
                  error={formik.errors.displayName && formik.touched.displayName}
                  helperText={formik.touched.displayName && formik.errors.displayName}
                  required
                />
              </div>
              <OutlinedInput
                id="description"
                label="Description"
                {...formik.getFieldProps('description')}
                value={formik.values.description || ''}
              />
              <div className={commonStyles.twoColumn}>
                <OutlinedInput
                  id="addressOne"
                  label="Address"
                  {...formik.getFieldProps('addressOne')}
                  error={formik.errors.addressOne && formik.touched.addressOne}
                  helperText={formik.touched.addressOne && formik.errors.addressOne}
                  required
                />
                <OutlinedInput
                  id="addressTwo"
                  label="Address Two"
                  {...formik.getFieldProps('addressTwo')}
                  value={formik.values.addressTwo || ''}
                />
              </div>
              <div className={commonStyles.threeColumn}>
                <OutlinedInput
                  id="city"
                  label="City"
                  {...formik.getFieldProps('city')}
                  error={formik.errors.city && formik.touched.city}
                  helperText={formik.touched.city && formik.errors.city}
                  required
                />
                <OutlinedSelect
                  id="state"
                  title="State"
                  value={formik.values.state}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  options={[
                    <option key="none" value={0} />,
                    ...usStates.map((state, index) => (
                      <option key={state.abbreviation} value={index + 1}>
                        {state.name}
                      </option>
                    ))
                  ]}
                  error={'state' in formik.errors && formik.touched.state}
                  selectProps={{ displayEmpty: true }}
                  required
                />
                <OutlinedInput
                  id="zipCode"
                  label="Zip Code"
                  {...formik.getFieldProps('zipCode')}
                  error={formik.errors.zipCode && formik.touched.zipCode}
                  helperText={formik.touched.zipCode && formik.errors.zipCode}
                  required
                />
              </div>
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <h2 className={styles.formSectionHeading}>Primary Contact</h2>
              <div className={commonStyles.twoColumn}>
                <OutlinedInput
                  id="firstName"
                  label="First Name"
                  {...formik.getFieldProps('firstName')}
                  error={formik.errors.firstName && formik.touched.firstName}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                  required
                />
                <OutlinedInput
                  id="lastName"
                  label="Last Name"
                  {...formik.getFieldProps('lastName')}
                  error={formik.errors.lastName && formik.touched.lastName}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  required
                />
              </div>
              <div className={commonStyles.twoColumn}>
                <OutlinedInput
                  id="email"
                  label="Email"
                  {...formik.getFieldProps('email')}
                  error={formik.errors.email && formik.touched.email}
                  helperText={formik.touched.email && formik.errors.email}
                  required
                />
                <OutlinedInput
                  id="phone"
                  label="Phone"
                  {...formik.getFieldProps('phone')}
                  error={formik.errors.phone && formik.touched.phone}
                  helperText={formik.touched.phone && formik.errors.phone}
                  InputProps={{ inputComponent: PhoneInputMask as any }}
                  required
                />
              </div>
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <h2 className={styles.formSectionHeading}>Configuration</h2>
              <OutlinedInput
                id="supportEmail"
                label="Support Email"
                {...formik.getFieldProps('supportEmail')}
                error={formik.errors.supportEmail && formik.touched.supportEmail}
                helperText={formik.errors.supportEmail && formik.touched.supportEmail && formik.errors.supportEmail}
                required
              />
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <h2 className={styles.formSectionHeading}>Settings</h2>
              <SwitchBox id="status" value={formik.values.status} handleChange={formik.handleChange} label="Status" />
              <SwitchBox
                id="enableServiceDesk"
                value={formik.values.enableServiceDesk}
                handleChange={formik.handleChange}
                label="Service Desk"
              />
              <SwitchBox
                id="enableSegmentation"
                value={formik.values.enableSegmentation}
                handleChange={formik.handleChange}
                label="Fan Segmentation"
              />
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <h2 className={styles.formSectionHeading}>Branding</h2>
              <div style={{ marginTop: 16 }}>
                {brandImageSrc !== '' && (
                  <div>
                    <img
                      src={brandImageSrc}
                      alt="Brand Image"
                      style={{ maxWidth: 48, height: 'auto', marginBottom: 16 }}
                    />
                  </div>
                )}
                <input
                  accept="image/*"
                  className={classes.brandingInput}
                  id="brandImage"
                  multiple
                  type="file"
                  onChange={(event) => {
                    setBrandImageFile(event.target.files[0]);
                  }}
                />
                <label htmlFor="brandImage">
                  <SecondaryButton
                    variant="contained"
                    component="span"
                    text="Choose Company Logo File"
                    title="Choose Company Logo File"
                    preventDefault={false}
                    handleClick={() => false}
                  />
                </label>
              </div>
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <AdministratorTransfer client={client} setFieldValue={formik.setFieldValue} />
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <SupportTypeTransfer client={client} setFieldValue={formik.setFieldValue} supportType="organizations" />
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <SupportTypeTransfer client={client} setFieldValue={formik.setFieldValue} supportType="request-types" />
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <ServiceDeskFields client={client} setFieldValue={formik.setFieldValue} />
            </fieldset>
            <div className={commonStyles.formActions}>
              <TertiaryButton
                text="Cancel"
                title="Cancel"
                component="button"
                handleClick={() => {
                  handleCancel(formik.dirty);
                }}
              />
              <span className={commonStyles.submitButton}>
                <PrimaryButton
                  text="Save"
                  title="Save"
                  deactive={formik.isSubmitting || !formik.isValid}
                  handleClick={formik.handleSubmit}
                />
              </span>
            </div>
          </form>
        )}
      </Formik>
    </React.Fragment>
  );
};
export default withRouter(ClientManager);
